import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import React from 'react';
import { type ComponentProps } from 'react';

type LinkWithQueryProps = ComponentProps<typeof Link> & {
	preserveQuery?: boolean;
	excludeParams?: string[];
};

/**
 * Link that preserves current query string. Uses prefetch={false} by default
 * to avoid preloading many pages when used in footers/headers with lots of links.
 *
 * @param preserveQuery - Whether to preserve the current query string (default: true).
 * @param excludeParams - List of query parameter keys to remove from the preserved query string.
 */
export const LinkWithQuery = React.forwardRef<
	HTMLAnchorElement,
	LinkWithQueryProps
>(
	(
		{ href, preserveQuery = true, excludeParams, prefetch = false, ...props },
		ref,
	) => {
		const searchParams = useSearchParams();
		let finalHref = href;

		if (preserveQuery) {
			const params = new URLSearchParams(searchParams.toString());

			excludeParams?.forEach(key => params.delete(key));

			const queryString = params.toString();
			if (queryString) {
				const base = typeof href === 'string' ? href : href.pathname ?? '';
				finalHref = `${base}${base.includes('?') ? '&' : '?'}${queryString}`;
			}
		}

		return <Link ref={ref} href={finalHref} prefetch={prefetch} {...props} />;
	},
);
